import {
  ContentNode,
  ContentNodeUtils,
  DataTypeLocator,
  ListContentNode,
  ObjectContentNode,
  QueryNode,
} from '@helo/cms'

import {
  ListTableProbeResult,
  ListTablesTest,
  ProbeStatus,
} from '../ListTablesTestPage/ListTablesTestPage.parts'

/** Rows fetched per page while searching on the fail path (fewer round-trips). */
export const PROBE_PAGE_SIZE = 500

/** Parents probed in parallel within each fetched parent page. */
export const PROBE_PARENT_CHECK_CONCURRENCY = 6

/** Minimal row count to verify a table loads with data. */
export const PROBE_INITIAL_ROW_COUNT = 1

export type ProbeRunResult = {
  status: ProbeStatus
  totalCount?: number
  itemCount?: number
  sampleValue?: string
  recordsChecked?: number
  passRecordPath?: string
  passEntityId?: string
  errorMessage?: string
}

export type ListRecordLinkConfig = {
  detailPathTemplate?: string
  entityIdDataKey?: string
}

export const LIST_RECORD_LINK_BY_QUERY_ID: Record<
  string,
  ListRecordLinkConfig
> = {
  containersList: {
    detailPathTemplate: '/containers/:id',
    entityIdDataKey: 'containerIRI',
  },
  fclContainersList: {
    detailPathTemplate: '/fcl-containers/:id',
    entityIdDataKey: 'containerIRI',
  },
  shipmentsList: {
    detailPathTemplate: '/shipments/:id',
    entityIdDataKey: 'shipmentDocumentPk',
  },
  ordersList: {
    detailPathTemplate: '/orders/:id',
    entityIdDataKey: 'purchaseOrderPk',
  },
  itemsList: {
    detailPathTemplate: '/items/:id',
    entityIdDataKey: 'catalogItemPk',
  },
  containerOrderItemList: {},
}

const buildPassFromListResult = (options: {
  probeResult: ListTableProbeResult
  queryNode: QueryNode | undefined
  connectionLocator: DataTypeLocator
  listViewPath: string
  queryId?: string
  entityIdDataKey?: string
  recordsChecked: number
  totalCount: number
}): ProbeRunResult => {
  const {
    probeResult,
    queryNode,
    connectionLocator,
    listViewPath,
    queryId,
    entityIdDataKey,
    recordsChecked,
    totalCount,
  } = options

  const firstRow = TableProbeRunner.extractListPageItems(
    queryNode,
    connectionLocator,
  ).items[0]
  const passEntityId =
    entityIdDataKey && firstRow
      ? TableProbeRunner.extractEntityIdFromRow(firstRow, entityIdDataKey)
      : undefined

  return {
    status: 'pass',
    totalCount,
    itemCount: probeResult.itemCount,
    sampleValue: probeResult.sampleValue,
    recordsChecked,
    passEntityId,
    passRecordPath: TableProbeRunner.buildRecordPath(listViewPath, {
      queryId,
      entityId: passEntityId,
    }),
  }
}

export const TableProbeRunner = {
  buildRecordPath: (
    listViewPath: string,
    options: {
      queryId?: string
      entityId?: string
      detailViewPath?: string
    },
  ): string => {
    const { queryId, entityId, detailViewPath } = options

    if (detailViewPath && entityId) {
      return detailViewPath.replace(':id', entityId)
    }

    const linkConfig = queryId
      ? LIST_RECORD_LINK_BY_QUERY_ID[queryId]
      : undefined

    if (linkConfig?.detailPathTemplate && entityId) {
      return linkConfig.detailPathTemplate.replace(':id', entityId)
    }

    return listViewPath
  },

  buildAbsoluteRecordUrl: (path: string): string =>
    `${window.location.origin}${path.startsWith('/') ? path : `/${path}`}`,

  extractEntityIdFromRow: (
    row: ContentNode,
    entityIdDataKey: string,
  ): string | undefined => {
    if (!ContentNodeUtils.isKind('OBJECT')(row)) {
      return undefined
    }

    const entityId = row.data?.[entityIdDataKey]

    return typeof entityId === 'string' && entityId.length > 0
      ? entityId
      : undefined
  },

  extractListPageItems: (
    queryNode: QueryNode | undefined,
    connectionLocator: DataTypeLocator,
  ): { totalCount: number; items: ObjectContentNode[] } => {
    if (!queryNode?.queryContentNode) {
      return { totalCount: 0, items: [] }
    }

    const connectionContentNode = ContentNodeUtils.findContentNode(
      connectionLocator,
    )(queryNode.queryContentNode) as ObjectContentNode | null

    if (!connectionContentNode) {
      return { totalCount: 0, items: [] }
    }

    const itemsListContentNode = ContentNodeUtils.findContentNode([
      ['key', 'items'],
      ['typeName', 'List'],
    ])(connectionContentNode) as ListContentNode | null

    const payload = connectionContentNode.data as { totalCount?: number } | null
    const items = (itemsListContentNode?.items ?? []).filter(
      (item): item is ObjectContentNode =>
        ContentNodeUtils.isKind('OBJECT')(item),
    )
    const totalCount = payload?.totalCount ?? items.length

    return { totalCount, items }
  },

  hasTableRows: (
    queryNode: QueryNode | undefined,
    connectionLocator: DataTypeLocator,
    sampleColumnSelector?: DataTypeLocator,
  ): ListTableProbeResult | undefined => {
    const probeResult = ListTablesTest.extractProbeResult(
      queryNode,
      connectionLocator,
      sampleColumnSelector,
    )

    return probeResult.itemCount >= 1 ? probeResult : undefined
  },

  runPaginatedListProbe: async (options: {
    fetchPage: (skip: number, first: number) => Promise<QueryNode | undefined>
    connectionLocator: DataTypeLocator
    sampleColumnSelector?: DataTypeLocator
    listViewPath: string
    queryId?: string
    entityIdDataKey?: string
    onProgress?: (recordsChecked: number, totalCount: number) => void
  }): Promise<ProbeRunResult> => {
    const {
      fetchPage,
      connectionLocator,
      sampleColumnSelector,
      listViewPath,
      queryId,
      entityIdDataKey,
      onProgress,
    } = options

    let skip = 0
    let totalCount = 0
    let recordsChecked = 0
    let isFirstFetch = true

    while (true) {
      const pageSize = isFirstFetch ? PROBE_INITIAL_ROW_COUNT : PROBE_PAGE_SIZE
      const queryNode = await fetchPage(skip, pageSize)
      const page = TableProbeRunner.extractListPageItems(
        queryNode,
        connectionLocator,
      )

      totalCount = Math.max(totalCount, page.totalCount)
      recordsChecked += Math.max(page.items.length, isFirstFetch ? pageSize : 0)
      onProgress?.(
        Math.min(recordsChecked, totalCount || recordsChecked),
        totalCount,
      )

      const passResult = TableProbeRunner.hasTableRows(
        queryNode,
        connectionLocator,
        sampleColumnSelector,
      )

      if (passResult) {
        return buildPassFromListResult({
          probeResult: passResult,
          queryNode,
          connectionLocator,
          listViewPath,
          queryId,
          entityIdDataKey,
          recordsChecked: skip + Math.max(page.items.length, 1),
          totalCount,
        })
      }

      if (totalCount === 0 || skip + pageSize >= totalCount) {
        break
      }

      skip += pageSize
      isFirstFetch = false
    }

    return {
      status: 'fail',
      totalCount,
      itemCount: 0,
      recordsChecked: totalCount > 0 ? totalCount : recordsChecked,
      errorMessage:
        totalCount === 0
          ? 'No records in list for this environment'
          : `Checked all ${totalCount} records — none returned list rows`,
    }
  },
}
